import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Toaster, toast } from "sonner";
import { Check, Send, Loader2 } from "lucide-react";

const API = '/api';

const GAME_TYPES = {
  V65: { legs: 6, label: 'V65', pricePerRow: 1, hasBongavgift: true },
  V75: { legs: 7, label: 'V75', pricePerRow: 0.5, hasBongavgift: false },
  DD: { legs: 2, label: 'DD', pricePerRow: 1, hasBongavgift: false },
};

/**
 * Shared bong input page for both Tipsekspert and Gjest.
 * @param {string} role - "tips" or "guest"
 * @param {string} defaultName - Default display name
 * @param {string} stateKey - Backend key: "bong_data" or "bong_data_guest"
 */
export default function BongInputPage({ role = "tips", defaultName = "Tipsekspert", stateKey = "bong_data" }) {
  const [gameType, setGameType] = useState('V65');
  const [legs, setLegs] = useState({});
  const [name, setName] = useState(defaultName);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [connected, setConnected] = useState(null);

  const legsCount = GAME_TYPES[gameType]?.legs || 6;

  // Health check
  useEffect(() => {
    const check = async () => {
      try {
        await axios.get(`${API}/health`, { timeout: 5000 });
        setConnected(true);
      } catch {
        setConnected(false);
      }
    };
    check();
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, []);

  // Reset legs when game type changes
  useEffect(() => {
    const count = GAME_TYPES[gameType]?.legs || 6;
    const newLegs = {};
    for (let i = 1; i <= count; i++) {
      newLegs[i] = legs[i] || '';
    }
    setLegs(newLegs);
  }, [gameType]);

  // Load existing bong from backend on mount
  useEffect(() => {
    const loadExisting = async () => {
      try {
        const res = await axios.get(`${API}/overlay-state`);
        const bd = res.data?.[stateKey];
        if (bd?.legs) {
          if (bd.gameType && GAME_TYPES[bd.gameType]) {
            setGameType(bd.gameType);
          }
          if (bd.ownerName) {
            setName(bd.ownerName);
          }
          const count = GAME_TYPES[bd.gameType]?.legs || 6;
          const loaded = {};
          for (let i = 1; i <= count; i++) {
            loaded[i] = bd.legs[i] || '';
          }
          setLegs(loaded);
        }
      } catch { /* ignore */ }
    };
    loadExisting();
  }, [stateKey]);

  const calculateRows = useCallback(() => {
    let smallCombinations = 1;
    let largeCombinations = 1;
    for (let i = 1; i <= legsCount; i++) {
      const value = legs[i] || '';
      if (value.includes('/')) {
        const [leftPart, rightPart] = value.split('/');
        const leftHorses = leftPart.split(',').filter(h => h.trim()).length;
        const rightNums = rightPart.split(',').map(h => h.trim()).filter(h => h);
        const leftNums = new Set(leftPart.split(',').map(h => h.trim()).filter(h => h));
        const allNums = new Set([...leftNums, ...rightNums]);
        if (leftHorses > 0) smallCombinations *= leftHorses;
        if (allNums.size > 0) largeCombinations *= allNums.size;
      } else {
        const horses = value.split(',').filter(h => h.trim()).length;
        if (horses > 0) {
          smallCombinations *= horses;
          largeCombinations *= horses;
        }
      }
    }
    return { small: smallCombinations, large: largeCombinations };
  }, [legs, legsCount]);

  const hasSplit = useCallback(() => {
    for (let i = 1; i <= legsCount; i++) {
      if ((legs[i] || '').includes('/')) return true;
    }
    return false;
  }, [legs, legsCount]);

  const saveBong = async () => {
    setSaving(true);
    try {
      const resolvedLegs = {};
      for (let i = 1; i <= legsCount; i++) {
        resolvedLegs[i] = legs[i] || '';
      }
      const bankerHorses = {};
      for (let i = 1; i <= legsCount; i++) {
        const value = resolvedLegs[i];
        const smallPart = value.includes('/') ? value.split('/')[0] : value;
        const selected = smallPart.split(',').map(h => h.trim()).filter(h => h);
        if (selected.length === 1) {
          bankerHorses[i] = { number: parseInt(selected[0]), name: `Hest ${selected[0]}` };
        }
      }
      const rows = calculateRows();
      const { pricePerRow, hasBongavgift } = GAME_TYPES[gameType];
      const bongavgift = (innsats) => innsats <= 0 ? 0 : Math.min(Math.ceil(innsats / 50), 5);
      const rawSmall = rows.small * pricePerRow;
      const rawLarge = rows.large * pricePerRow;
      const bongInfo = {
        gameType,
        trackName: 'Sørlandet',
        ownerName: name.trim() || defaultName,
        legs: resolvedLegs,
        horseNames: {},
        bankerHorses,
        totalHorsesPerLeg: {},
        cost: rawSmall + (hasBongavgift ? bongavgift(rawSmall) : 0),
        costLarge: hasSplit() ? rawLarge + (hasBongavgift ? bongavgift(rawLarge) : 0) : null,
        hasSplit: hasSplit(),
        timestamp: Date.now()
      };

      await axios.post(`${API}/overlay-state`, { [stateKey]: bongInfo });
      setLastSaved(new Date());
      toast.success('Bong sendt til kontrollpanelet!');
    } catch (err) {
      toast.error('Kunne ikke sende bong');
    } finally {
      setSaving(false);
    }
  };

  const rows = calculateRows();
  const split = hasSplit();
  const { pricePerRow, hasBongavgift } = GAME_TYPES[gameType];
  const calcBongavgift = (innsats) => innsats <= 0 ? 0 : Math.min(Math.ceil(innsats / 50), 5);
  const rawSmall = rows.small * pricePerRow;
  const rawLarge = rows.large * pricePerRow;
  const cost = {
    small: rawSmall + (hasBongavgift ? calcBongavgift(rawSmall) : 0),
    large: rawLarge + (hasBongavgift ? calcBongavgift(rawLarge) : 0),
  };

  return (
    <div data-testid={`${role}-page`} style={{ minHeight: '100vh', backgroundColor: '#0A0F1C' }}>
      <Toaster position="top-center" richColors />
      <div style={{ maxWidth: '480px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/sorlandet-logo.png" alt="Sørlandets Travpark" style={{ height: '64px', margin: '0 auto 12px' }} />
          <h1 style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: '1.8rem', fontWeight: 700, color: '#FFD700',
            textTransform: 'uppercase', letterSpacing: '0.05em'
          }}>
            {role === 'guest' ? 'Gjestebong' : 'Tipsekspert'}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '8px' }}>
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%',
              backgroundColor: connected === true ? '#22C55E' : connected === false ? '#EF4444' : '#F59E0B'
            }} />
            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
              {connected === true ? 'Tilkoblet' : connected === false ? 'Frakoblet' : 'Kobler til...'}
            </span>
          </div>
        </div>

        {/* Name Input */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginBottom: '6px', display: 'block' }}>
            Visningsnavn
          </label>
          <Input
            data-testid={`${role}-name-input`}
            type="text"
            placeholder={defaultName}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-[#1E293B] border-[#334155] text-white"
            style={{ fontSize: '1.1rem' }}
          />
        </div>

        {/* Game Type Selector */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          {Object.entries(GAME_TYPES).map(([key, val]) => (
            <button
              key={key}
              data-testid={`${role}-gametype-${key}`}
              onClick={() => setGameType(key)}
              style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.05em',
                textTransform: 'uppercase', cursor: 'pointer',
                border: `2px solid ${gameType === key ? '#FFD700' : 'rgba(255,215,0,0.2)'}`,
                backgroundColor: gameType === key ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.03)',
                color: gameType === key ? '#FFD700' : 'rgba(255,255,255,0.5)',
                transition: 'all 0.2s',
              }}
            >
              {val.label}
            </button>
          ))}
        </div>

        {/* Leg Inputs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
          {Array.from({ length: legsCount }, (_, i) => i + 1).map((leg) => (
            <div key={leg} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '56px', height: '40px',
                backgroundColor: '#FFD700', color: '#0A1628',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700, fontSize: '0.95rem', borderRadius: '6px',
                flexShrink: 0,
              }}>
                {gameType}-{leg}
              </div>
              <Input
                data-testid={`${role}-leg-${leg}`}
                type="text"
                placeholder="1,2,3"
                value={legs[leg] || ''}
                onChange={(e) => setLegs({ ...legs, [leg]: e.target.value })}
                className="bg-[#1E293B] border-[#334155] text-white text-lg"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.1rem' }}
              />
            </div>
          ))}
        </div>

        <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', marginBottom: '16px', lineHeight: 1.5 }}>
          Skriv hestenummer separert med komma. Bruk / for liten/stor bong (f.eks. 1,2/3,4,5)
        </p>

        {/* Cost Calculator */}
        <div style={{
          padding: '16px', borderRadius: '10px',
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,215,0,0.15)',
          marginBottom: '24px',
        }}>
          {split ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>Rekker:</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#fff', fontSize: '1.05rem' }}>{rows.small} / {rows.large}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>Kostnad:</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#FFD700', fontSize: '1.2rem', fontWeight: 700 }}>
                  {cost.small.toFixed(2)} kr / {cost.large.toFixed(2)} kr
                </span>
              </div>
              <div style={{ textAlign: 'right', marginTop: '2px' }}>
                <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>liten / stor bong</span>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>Rekker:</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#fff', fontSize: '1.05rem' }}>{rows.small}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>Kostnad:</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#FFD700', fontSize: '1.2rem', fontWeight: 700 }}>{cost.small.toFixed(2)} kr</span>
              </div>
            </>
          )}
        </div>

        {/* Submit Button */}
        <Button
          data-testid={`${role}-submit`}
          onClick={saveBong}
          disabled={saving || !connected}
          style={{
            width: '100%', padding: '14px',
            backgroundColor: '#FFD700', color: '#0A1628',
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: '1.15rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            borderRadius: '8px', border: 'none', cursor: 'pointer',
          }}
          className="hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin inline" /> Sender...</>
          ) : (
            <><Send className="w-5 h-5 mr-2 inline" /> Send Bong</>
          )}
        </Button>

        {lastSaved && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '12px' }}>
            <Check className="w-4 h-4" style={{ color: '#22C55E' }} />
            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
              Sist sendt: {lastSaved.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
